import Badge, { BadgeProps } from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import ContentLayout from "@cloudscape-design/components/content-layout"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import Table from "@cloudscape-design/components/table"
import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import type { Study } from "../../services/daeApi"
import NotificationCard from "../../components/Notifications/NotificationCard"
import { useAuth } from "../../contexts/AuthContext"
import { useNotifications } from "../../contexts/NotificationContext"
import { useDashboardStats, useStudiesNeedingAttention } from "../../hooks/queries/useDashboard"
import { useStudiesList } from "../../hooks/queries/useStudies"
import HealthPanel from "./HealthPanel"
import VerifAIPanel from "./VerifAIPanel"
import "./DaeDashboard.css"

// Number of studies to surface in the dashboard quick-access list.
const QUICK_ACCESS_STUDY_COUNT = 5

const studyStatusColor = (status: string): NonNullable<BadgeProps["color"]> => {
  switch (status.toUpperCase()) {
    case "ACTIVE":
    case "COMPLETED":
      return "green"
    case "ON_HOLD":
    case "ONBOARDED":
      return "blue"
    case "ACTION_NEEDED":
      return "red"
    default:
      return "severity-neutral"
  }
}

const DaeDashboard = () => {
  const { user } = useAuth()
  const { notifications, loading: notificationsLoading, markAsRead } = useNotifications()
  const navigate = useNavigate()
  const daeId = user?.isAuthenticated ? user.principalId : undefined

  const {
    data: stats = {
      studies: { total: 0, active: 0, health: { atRisk: 0, watch: 0, healthy: 0 } },
      dtas: { total: 0, active: 0, pendingReviews: 0, pendingValidations: 0 },
      vendors: { active: 0 },
    },
    isPending: loading,
  } = useDashboardStats(daeId)

  const { data: studiesData, isLoading: studiesLoading } = useStudiesList(daeId)

  // Studies needing attention (failed/partially-completed ingestion runs or quarantined records).
  // Same source the My Studies filter reads (one definition), so the filtered rows converge to this
  // tile count once My Studies has loaded every study page.
  const { data: studiesNeedingAttention, isLoading: attentionLoading } =
    useStudiesNeedingAttention(daeId)

  // Only the first page is needed for the quick-access table.
  // All counts come from the dashboard-info endpoint (server-side).

  const allStudies = useMemo(
    () => studiesData?.pages.flatMap((p) => p.studies) ?? [],
    [studiesData],
  )

  // Portfolio-level metrics derived from the studies collection. These complement the
  // data-stream/vendor stats and are not otherwise surfaced on the dashboard. A "+" suffix
  // signals the counts are still growing while remaining pages load.
  const studyMetrics = useMemo(() => {
    return {
      totalStudies: `${stats.studies.total}`,
      activeStudies: `${stats.studies.active}`,
      studiesNeedingAttention: `${studiesNeedingAttention?.size ?? 0}`,
    }
  }, [stats, studiesNeedingAttention])

  const quickAccessStudies = useMemo(
    () => allStudies.slice(0, QUICK_ACCESS_STUDY_COUNT),
    [allStudies],
  )

  // Deduplicate notifications: keep only most recent per subject+study_id combo
  const dedupedNotifications = useMemo(() => {
    const seen = new Set<string>()
    return notifications.filter((n) => {
      const key = `${n.study_id}:${n.notification_type}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [notifications])

  return (
    <ContentLayout header={<Header variant="h1">Your Portfolio Dashboard</Header>}>
      <Grid
        gridDefinition={[{ colspan: { default: 12, s: 7 } }, { colspan: { default: 12, s: 5 } }]}
      >
        {/* Main Column - Metrics + quick-access studies */}
        <SpaceBetween size="l">
          {/* Health Metrics */}
          <HealthPanel
            stats={stats}
            loading={loading}
            studyMetrics={studyMetrics}
            studiesLoading={studiesLoading}
            attentionLoading={attentionLoading}
          />

          {/* Quick-access studies */}
          <Container
            header={
              <Header
                variant="h2"
                description="Jump back into your studies"
                actions={
                  <Button
                    variant="inline-link"
                    iconName="arrow-right"
                    iconAlign="right"
                    onClick={() => navigate("/studies")}
                  >
                    All Studies
                  </Button>
                }
              >
                Studies
              </Header>
            }
          >
            <div className="dae-study-table">
              <Table<Study>
                variant="embedded"
                items={quickAccessStudies}
                trackBy="study_id"
                onRowClick={({ detail }) => navigate(`/studies/${detail.item.study_id}`)}
                loading={studiesLoading && quickAccessStudies.length === 0}
                loadingText="Loading..."
                empty={
                  <Box textAlign="center" color="text-status-inactive">
                    No studies shared with you yet
                  </Box>
                }
                ariaLabels={{ tableLabel: "Studies" }}
                columnDefinitions={[
                  {
                    id: "name",
                    header: "Study",
                    cell: (study) => study.study_name,
                  },
                  {
                    id: "meta",
                    header: "Status",
                    cell: (study) => (
                      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                        <Badge color={studyStatusColor(study.study_status)}>
                          {study.study_status.replace(/_/g, " ")}
                        </Badge>
                        <Box color="text-body-secondary" fontSize="body-s">
                          {study.dta_count ?? 0} DTA
                          {(study.dta_count ?? 0) === 1 ? "" : "s"}
                        </Box>
                      </SpaceBetween>
                    ),
                  },
                ]}
              />
            </div>
          </Container>
        </SpaceBetween>

        {/* Side Column - VerifAI insights on top, Notifications beneath */}
        <SpaceBetween size="l">
          <VerifAIPanel />
          <Container
            header={
              <Header
                variant="h2"
                actions={
                  <Button
                    variant="inline-link"
                    iconName="arrow-right"
                    iconAlign="right"
                    onClick={() => navigate("/notifications")}
                  >
                    All Notifications
                  </Button>
                }
              >
                Notifications
              </Header>
            }
          >
            <div className="dae-notification-list">
              {notificationsLoading && notifications.length === 0 ? (
                <Box textAlign="center" padding="l" color="text-status-inactive">
                  <SpaceBetween size="s" alignItems="center">
                    <Spinner />
                    <span>Loading...</span>
                  </SpaceBetween>
                </Box>
              ) : dedupedNotifications.filter((n) => n.read_status === "UNREAD").length === 0 ? (
                <Box textAlign="center" padding="l" color="text-status-inactive">
                  No unread notifications
                </Box>
              ) : (
                <SpaceBetween size="s">
                  {dedupedNotifications
                    .filter((n) => n.read_status === "UNREAD")
                    .slice(0, 5)
                    .map((notif) => (
                      <NotificationCard
                        key={notif.notification_id}
                        notification={notif}
                        onMarkAsRead={markAsRead}
                        compact
                        highlightUnread={false}
                      />
                    ))}
                </SpaceBetween>
              )}
            </div>
          </Container>
        </SpaceBetween>
      </Grid>
    </ContentLayout>
  )
}

export default DaeDashboard
